<template>
    <div class="[ bg-gray-pale ]">
        <Teleport to="[data-header-action]" v-if="isMounted">
            <div class="[ flex gap-2 ]">
                <ButtonComponent outlined label="Import Template" @click="openImportDialog">
                    <template #icon>
                        <JBIcon name="cloud-upload" />
                    </template>
                </ButtonComponent>
                <ButtonComponent outlined label="New Template" @click="openNewTemplateDialog">
                    <template #icon>
                        <JBIcon name="add" />
                    </template>
                </ButtonComponent>
            </div>
        </Teleport>
        <div>
            <DataTable :value="scorecardTemplates" selection-mode="single" @row-select="onRowSelected"
                sort-mode="single" sort-field="dateCreated" :sort-order="-1">
                <template #empty>
                    <JBLoadingSpinner v-if="loading" />
                    <Empty v-else icon="unavailable" title="No scorecard templates yet"
                        message="Create a new scorecard template to get started">
                        <ButtonComponent label="New Scorecard Template" @click="navigateToNewScorecardTemplate">
                            <template #icon>
                                <JBIcon name="add" />
                            </template>
                        </ButtonComponent>
                    </Empty>
                </template>
                <Column field="id" header="Id" />
                <Column field="name" header="Name" style="width: 50%" />
                <Column header="Date &amp; time" field="dateCreated" sortable>
                    <template #body="{ data }: { data: ScorecardTemplate }">
                        <div class="[ flex gap-3 text-gray-dark ]">
                            <span class="[ border-r pr-3 ]">{{ readableDate(data.dateCreated!) }}</span>
                            <span>{{ getTimeFromDate(data.dateCreated!) }}</span>
                        </div>
                    </template>
                </Column>
                <Column header="Status" field="isDraft" sortable>
                    <template #body="{ data }: { data: ScorecardTemplate }">
                        <div class="[ text-gray-dark ]">
                            {{ data.isDraft ? 'Draft' : 'Published' }}
                        </div>
                    </template>
                </Column>
                <Column>
                    <template #body="{ data }: { data: ScorecardTemplate }">
                        <div class="[ flex gap-2 justify-end ]">
                            <ButtonComponent label="Delete" @click="startDelete(data)" />
                            <ButtonComponent label="Edit" @click="openEditTemplateDialog(data)" />
                            <ButtonComponent label="Clone" @click="openCloneTemplateDialog(data.id!)" />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
        <JBDialog v-model:visible="isImportDialogOpen" header="Import Templates">
            <div>
                <JBLoadingSpinner v-if="isImporting" />
                <UploadArea v-else :accept="['application/json']" @uploaded="onUpload" />
            </div>
        </JBDialog>
        <JBDialog v-model:visible="isNewDialogOpen" header="New Template">
            <ScorecardTemplateForm v-model:name="newName" v-model:parameters="newParameters" />
            <template #footer>
                <div class="[ flex justify-end gap-2 ]">
                    <SplitButton label="Save" @click="saveTemplate" :disabled="isSaving"
                        :model="[{ label: 'Save and Publish', command: saveTemplateAndPublish }]" />
                </div>
            </template>
        </JBDialog>
        <JBDialog v-model:visible="isEditDialogOpen" header="Edit Template">
            <ScorecardTemplateForm v-model:name="newName" v-model:parameters="newParameters" />
            <template #footer>
                <div class="[ flex justify-end gap-2 ]">
                    <SplitButton label="Save" @click="saveEditedTemplate" :disabled="isSaving"
                        :model="[{ label: selectedTemplate?.isDraft ? 'Save and Publish' : 'Save as Draft', command: selectedTemplate?.isDraft ? saveTemplateAndPublish : saveTemplate }]" />
                </div>
            </template>
        </JBDialog>
        <JBDialog v-model:visible="isDeleteConfirmationOpen" header="Delete Template">
            <div>Are you sure you want to delete <span class="[ text-lg ]">{{ templateSelectedForDeletion?.name }}? This
                    cannot
                    be undone.</span></div>
            <template #footer>
                <ButtonComponent severity="danger" label="Delete" @click="confirmDelete" :loading="isDeletingTemplate"
                    :disabled="isDeletingTemplate" />
            </template>
        </JBDialog>
        <JBDialog v-model:visible="isCloneDialogOpen" header="Clone Template">
            <GridForm size="full">
                <GridFormField id="name" label="Name">
                    <template #input="{ id }">
                        <InputText :id="id" v-model="cloneForm.name" />
                    </template>
                </GridFormField>
            </GridForm>
            <template #footer>
                <div class="[ flex justify-end ]">
                    <ButtonComponent label="Clone" @click="confirmClone" :loading="isCloningTemplate"
                        :disabled="!cloneForm.name || isCloningTemplate" />
                </div>
            </template>
        </JBDialog>
    </div>
</template>
<script setup lang="ts">
import { definePage } from 'vue-router/auto'
import { Button as ButtonComponent, SplitButton, InputText } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { Scorecard, ScorecardTemplate, ScorecardTemplateFromJSON, ScorecardTemplateSummary } from '@/services/api/sdk';
import { getTimeFromDate, readableDate } from '@/utils/date';
import { DataTable, Column, DataTableRowSelectEvent } from 'primevue';
import { onMounted, reactive, ref, unref } from 'vue';
import { useRouter } from 'vue-router';
import Empty from '@/components/ui/Empty.vue';
import JBLoadingSpinner from '@/components/ui/JBLoadingSpinner.vue';
import { useMounted } from '@vueuse/core';
import JBDialog from '@/components/ui/modals/JBDialog.vue';
import UploadArea from '@/components/ui/UploadArea.vue';
import ScorecardTemplateForm from '@/components/scorecard-templates/ScorecardTemplateForm.vue';
import GridForm from '@/components/ui/GridForm.vue';
import GridFormField from '@/components/ui/GridFormField.vue';


const isMounted = useMounted();

const router = useRouter();

const api = useApi();

const isImportDialogOpen = ref(false);
const isDeleteConfirmationOpen = ref(false);
const isNewDialogOpen = ref(false);
const isEditDialogOpen = ref(false);
const isImporting = ref(false);

const selectedTemplate = ref<ScorecardTemplateSummary | null>(null);
const newName = ref('');
const newParameters = ref('');
const newIsDraft = ref(true);

// function useCloning() {
//     const isCloneDialogOpen = ref(false);
//     const cloneSourceId = ref<number | null>(null);
//     const newName = ref('');

//     const { load: cloneScorecardTemplate, loading: isCloningTemplate } = useApiLoader(async () => {
//         if (!cloneSourceId.value) {
//             return null;
//         }
//         await api.cloneScorecardTemplate({
//             scorecardTemplateId: cloneSourceId.value,
//             cloneScorecardTemplateRequest: {
//                 name: newName.value!,
//             }
//         });

//         return null;
//     }, null);

//     function openCloneTemplateDialog(id: number) {
//         newName.value = '';
//         cloneSourceId.value = id;
//         isCloneDialogOpen.value = true;
//     }

//     async function confirmClone() {
//         await cloneScorecardTemplate();
//         isCloneDialogOpen.value = false;
//     }

//     return {
//         isCloneDialogOpen,
//         cloneSourceId,
//         newName,
//         isCloningTemplate,
//         openCloneTemplateDialog,
//         confirmClone
//     };
// }

// const {
//     isCloneDialogOpen,
//     newName: clonedName,
//     isCloningTemplate,
//     openCloneTemplateDialog,
//     confirmClone
// } = useCloning();

function useEntityAction<TEntity, TForm extends {}, TRes>(defaultFormFactory: () => TForm, actionFn: (entity: TEntity, form: TForm) => Promise<TRes>) {
    const isDialogOpen = ref(false);
    const form = reactive<TForm>(defaultFormFactory());
    const selectedEntity = ref<TEntity | null>();

    function openActionDialog(entity: TEntity) {
        selectedEntity.value = entity;
        isDialogOpen.value = true;
    }

    async function confirmAction() {
        await load();
    }

    const { load, loading, data } = useApiLoader(async () => {
        if (!selectedEntity.value) {
            return null;
        }
        await actionFn(unref(selectedEntity)!, unref(form));
        isDialogOpen.value = false;
    }, null);

    return {
        isDialogOpen,
        openActionDialog,
        confirmAction,
        form,
        loading,
    };
}

const {
    isDialogOpen: isCloneDialogOpen,
    form: cloneForm,
    loading: isCloningTemplate,
    openActionDialog: openCloneTemplateDialog,
    confirmAction: confirmClone
} = useEntityAction<number, { name: string }, void>(() => ({ name: '' }), async (scorecardTemplateId, { name }) => {
    await api.cloneScorecardTemplate({
        scorecardTemplateId,
        cloneScorecardTemplateRequest: {
            name,
        }
    });
});


function openNewTemplateDialog() {
    newName.value = '';
    newParameters.value = '';
    newIsDraft.value = true;
    isNewDialogOpen.value = true;
}

function openEditTemplateDialog(template: ScorecardTemplateSummary) {
    selectedTemplate.value = template;
    newName.value = template.name!;
    newParameters.value = template.parameters || '[]';
    newIsDraft.value = true;
    isEditDialogOpen.value = true;
}

const { load: saveScorecardTemplate, loading: isSaving } = useApiLoader(async () => {
    if (selectedTemplate.value === null) {
        const res = await api.createScorecardTemplate({
            createScorecardTemplateRequest: {
                name: newName.value,
                parameters: newParameters.value,
                isDraft: newIsDraft.value,
            },
        });

        return res.data;
    }

    const res = await api.updateScorecardTemplate({
        scorecardTemplateId: selectedTemplate.value.id!,
        updateScorecardTemplateRequest: {
            name: newName.value,
            parameters: newParameters.value,
            isDraft: newIsDraft.value,
        },
    });

    return res.data;

}, null);

async function saveTemplate() {
    await saveScorecardTemplate();
    isNewDialogOpen.value = false;
    isEditDialogOpen.value = false;
    await loadScorecardTemplates();
}

async function saveTemplateAndPublish() {
    newIsDraft.value = false;
    await saveScorecardTemplate();
    isNewDialogOpen.value = false;
    isEditDialogOpen.value = false;
    await loadScorecardTemplates();
}

async function saveEditedTemplate() {
    if (selectedTemplate.value?.isDraft) {
        await saveTemplate();
    } else {
        await saveTemplateAndPublish();
    }
}

const templateSelectedForDeletion = ref<ScorecardTemplateSummary | null>(null);

function startDelete(template: ScorecardTemplateSummary) {
    templateSelectedForDeletion.value = template;
    isDeleteConfirmationOpen.value = true;
}

const { load: deleteScorecardTemplate, loading: isDeletingTemplate } = useApiLoader(async () => {
    if (!templateSelectedForDeletion.value) {
        return null;
    }
    await api.deleteScorecardTemplate({
        scorecardTemplateId: templateSelectedForDeletion.value.id!,
    });

    return null;
}, null);

async function confirmDelete() {
    await deleteScorecardTemplate();
    isDeleteConfirmationOpen.value = false;
    templateSelectedForDeletion.value = null;
    await loadScorecardTemplates();
}

function openImportDialog() {
    isImportDialogOpen.value = true;
}

const readAsync = (f: File) => new Promise<ScorecardTemplate>((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => {
        const jsonStr = reader.result as string;
        const template = ScorecardTemplateFromJSON(JSON.parse(jsonStr));
        res(template);
    };
    reader.onerror = (e) => rej(e);
    reader.readAsText(f);
});

async function onUpload(files: File[]) {
    isImporting.value = true;
    for (const file of files) {
        const template = await readAsync(file);
        await api.importScorecard({
            scorecardTemplate: template,
        });
    }
    isImportDialogOpen.value = false;
    isImporting.value = false;
    await loadScorecardTemplates();
}

const { load: loadScorecardTemplates, data: scorecardTemplates, loading } = useApiLoader(async () => {
    const res = await api.listScorecardTemplates();

    return res.data;
}, null);

type ScorecardRow = Scorecard & {
    agentName: string;
    reviewerName: string;
};

function onRowSelected(ev: DataTableRowSelectEvent) {
    openScorecardTemplate(ev.data.id);
}

function navigateToNewScorecardTemplate() {
    router.push({
        name: 'new-scorecard-template',
    });
}

function openScorecardTemplate(id: number) {
    router.push({
        name: 'scorecard-template',
        params: {
            id,
        },
    });
}

onMounted(() => {
    loadScorecardTemplates();
});
definePage({
    name: "scorecard-templates",
    meta: { title: "Scorecard Templates", icon: "hierarchy-square" },
});
</script>